import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUserCountry, calculateDistance } from '../utils/locationUtils.js';

export const DataContext = createContext();

export function DataProvider({ children }) {
  const [states, setStates] = useState([]);
  const [venues, setVenues] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);

  const refreshEvents = useCallback(async () => {
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/allevents');
      if (!response.ok) throw new Error('Failed to fetch events');
      const data = await response.json();
      
      setAllEvents(data);
      setFilteredEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    }
  }, []);

  const refreshVenues = useCallback(async () => {
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/venues');
      if (!response.ok) throw new Error('Failed to fetch venues');
      const data = await response.json();
      setVenues(data);
    } catch (err) {
      console.error('Error fetching venues:', err);
      setError(err.message);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/states');
      if (!response.ok) throw new Error('Failed to fetch states');
      const data = await response.json();
      setStates(data);
    } catch (err) {
      console.error('Error fetching states:', err);
      setError(err.message);
    }
  }, []);

  const refreshLayouts = useCallback(async () => {
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/layouts');
      if (!response.ok) throw new Error('Failed to fetch layouts');
      const data = await response.json();
      setLayouts(data);
    } catch (err) {
      console.error('Error fetching layouts:', err);
      setError(err.message);
    }
  }, []);

  const refreshPartners = useCallback(async () => {    
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/partners');
      if (!response.ok) throw new Error('Failed to fetch partners');
      const data = await response.json();
      setPartners(data);
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError(err.message);
    }
  }, []);

  const refreshPromoters = useCallback(async () => {
    try {
      const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/organisers');
      if (!response.ok) throw new Error('Failed to fetch promoters');
      const data = await response.json();
      setPromoters(data);
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const findNearestCountry = (userLat, userLon, countries) => {
    // Filter out countries with invalid coordinates
    const validCountries = countries.filter(country => 
      typeof country.Latitude === 'number' && 
      typeof country.Longitude === 'number' &&
      !isNaN(country.Latitude) && 
      !isNaN(country.Longitude)
    );
    
    if (validCountries.length === 0) {
      return countries[0]; // Fallback to first country if no valid coordinates
    }

    return validCountries.reduce((nearest, country) => {
      const d = calculateDistance(
        userLat, 
        userLon, 
        country.Latitude, 
        country.Longitude
      );
      
      if (!nearest || d < nearest.distance) {
        return { country, distance: d };
      }
      return nearest;
    }, null)?.country;
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch('https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/countries');
        if (!response.ok) throw new Error('Failed to fetch countries');
        const data = await response.json();        
        const activeCountries = data.filter(country => country.IsActive === 1);
        setCountries(activeCountries);
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const detectUserLocation = async () => {
      try {
        const location = await getUserCountry();
        

        const matchingCountry = countries.find(
          c => c.CountryName.toUpperCase() === location.country.toUpperCase()
        );
        

        if (matchingCountry) {
          setSelectedCountry(matchingCountry);
          
        } else {
          const nearest = findNearestCountry(
            location.lat, 
            location.lon, 
            countries
          );
          
          setSelectedCountry(nearest);
        }
      } catch (error) {
        console.error('Error detecting location:', error);
        const nearest = findNearestCountry(
          -33.8688, // Sydney coordinates
          151.2093,
          countries
        );
        
        setSelectedCountry(nearest);
      }
    };

    if (countries.length > 0) {
      detectUserLocation();
    }
  }, [countries]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          refreshEvents(),
          refreshVenues(),
          fetchStates(),
          refreshLayouts(),
          refreshPartners(),
          refreshPromoters()
        ]);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshEvents, refreshVenues, fetchStates, refreshLayouts, refreshPartners, refreshPromoters]);

  const filterEventsByCountry = useCallback((countryId) => {
    
    if (!countryId || !allEvents.length) {
      
      setFilteredEvents(allEvents);
      return;
    }
    const filtered = allEvents.filter(event => {
      return event.CountryID === countryId || event.countryid === countryId;
    });
    
    setFilteredEvents(filtered);
  }, [allEvents]);

  useEffect(() => {
    filterEventsByCountry(selectedCountry?.CountryID);
  }, [selectedCountry, filterEventsByCountry]);

  return (
    <DataContext.Provider value={{ 
      events: filteredEvents,
      allEvents,
      filterEventsByCountry,
      states, 
      venues,
      layouts,
      partners,
      promoters,
      loading, 
      error, 
      refreshEvents,
      refreshVenues,
      refreshLayouts,
      refreshPartners,
      refreshPromoters,
      userCountry,
      setUserCountry,
      countries,
      selectedCountry,
      setSelectedCountry
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
} 